import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Spinner } from 'react-bootstrap';
import { ToggleOn, ToggleOff } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { configurationAPI } from '../services';

/**
 * #160017 (lệnh sếp 15/09/2026) — BẢNG CÔNG TẮC TỔNG của các tính năng mới.
 *
 * Nguyên văn sếp: *"Tính năng nếu có gì bất thường thì có thể tắt ngay ở admin.. chỉ một nút thôi,
 * chứ không phải ở nằm ở tất cả các game để mà phải đi tắt đi mở từng game như vậy"*.
 *
 * Trước đây muốn tắt một tính năng mới thì phải mò đúng dòng trong bảng cấu hình (hơn trăm dòng),
 * mở hộp thoại sửa, gõ lại chữ `true`/`false` rồi bấm lưu. Bảng này kéo đúng những dòng cờ tính
 * năng (nhóm `FEATURE_FLAG`, quy ước của việc #201153) lên đầu màn, mỗi tính năng đúng MỘT nút bấm
 * là tắt/bật ngay cho toàn hệ thống.
 *
 * KHÔNG đẻ cơ chế bật/tắt thứ hai (luật kế thừa #731598018): vẫn ghi vào đúng bảng cấu hình cũ qua
 * đúng cửa `PUT /configurations/:id` — cửa đó tự xoá tầng đệm nên bấm là có hiệu lực NGAY, không
 * phải chờ 5 phút.
 */
export default function FeatureFlagSwitchboard({ tieuDe = 'Công tắc tính năng mới', hienCanhTrong = false }) {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dangDoi, setDangDoi] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await configurationAPI.getConfigurations({ category: 'FEATURE_FLAG' });
      const rows = res?.data || res || [];
      // Công tắc TỔNG chỉ có nghĩa ở mức chung — dòng riêng của một quán không được hiện ở đây,
      // kẻo người bấm tưởng đang tắt toàn hệ thống mà thật ra chỉ tắt một quán.
      setFlags((Array.isArray(rows) ? rows : []).filter((c) => !c.branch_id));
    } catch (err) {
      console.error('Không tải được danh sách công tắc tính năng:', err);
      setFlags([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const doiTrangThai = async (flag) => {
    const batLen = flag.value !== 'true';
    setDangDoi(flag.id);
    try {
      await configurationAPI.updateConfiguration(flag.id, { value: String(batLen) });
      toast.success(
        batLen
          ? 'Đã BẬT tính năng này cho toàn hệ thống'
          : 'Đã TẮT tính năng này trên toàn hệ thống — có hiệu lực ngay',
      );
      await load();
    } catch (err) {
      console.error('Không đổi được công tắc tính năng:', err);
      toast.error(err?.message || 'Không đổi được công tắc tính năng');
    } finally {
      setDangDoi(null);
    }
  };

  // #115005/#120646 — ở màn riêng "Tính Năng Mới" thì lúc chưa có tính năng nào vẫn phải hiện một
  // cảnh trống có lời giải thích (sếp đã duyệt cảnh này trong bản vẽ), chứ không được để trang trắng.
  if (!loading && flags.length === 0) {
    if (!hienCanhTrong) return null;
    return (
      <Card className="mb-4" data-testid="cong-tac-tinh-nang-trong">
        <Card.Body className="text-center text-muted py-5">
          <div style={{ fontSize: 38 }}>🧩</div>
          <p className="fw-semibold mt-3 mb-1 text-body">Chưa có tính năng mới nào để bật</p>
          <p className="mb-0 small">
            Khi đội kỹ thuật đưa lên một tính năng mới cần anh duyệt bật, nó sẽ tự hiện ở đây kèm mô
            tả và một nút bật/tắt.
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-4" data-testid="cong-tac-tinh-nang">
      {tieuDe ? (
        <Card.Header>
          <h5 className="mb-0">{tieuDe}</h5>
          <small className="text-muted">
            Tính năng có gì bất thường thì tắt ngay tại đây — một nút, áp cho mọi quán, mọi bàn.
          </small>
        </Card.Header>
      ) : null}
      <Card.Body>
        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" /> Đang xem...
          </div>
        ) : (
          flags.map((flag) => {
            const dangBat = flag.value === 'true';
            return (
              <div
                key={flag.id}
                className="d-flex flex-wrap align-items-center justify-content-between gap-2 border rounded p-3 mb-2"
                data-testid={`cong-tac-${flag.key}`}
              >
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="fw-semibold d-flex align-items-center gap-2 flex-wrap">
                    {flag.description || flag.key}
                    <Badge bg={dangBat ? 'success' : 'secondary'} data-testid={`trang-thai-${flag.key}`}>
                      {dangBat ? 'ĐANG BẬT' : 'ĐANG TẮT'}
                    </Badge>
                  </div>
                  <small className="text-muted d-block text-break">{flag.key}</small>
                </div>
                <Button
                  variant={dangBat ? 'danger' : 'success'}
                  disabled={dangDoi === flag.id}
                  onClick={() => doiTrangThai(flag)}
                  className="d-flex align-items-center gap-2"
                  data-testid={`nut-${flag.key}`}
                >
                  {dangDoi === flag.id ? (
                    <Spinner animation="border" size="sm" />
                  ) : dangBat ? (
                    <ToggleOff size={18} />
                  ) : (
                    <ToggleOn size={18} />
                  )}
                  {dangBat ? 'Tắt ngay' : 'Bật lên'}
                </Button>
              </div>
            );
          })
        )}
        <Alert variant="light" className="mb-0 mt-2 small">
          Tắt ở đây là tắt cho <b>mọi quán, mọi bàn</b> và có hiệu lực ngay lập tức, kể cả với khách
          đang dùng dở. Bật lại thì mọi thứ trở về đúng như trước, không mất dữ liệu đã nạp.
        </Alert>
      </Card.Body>
    </Card>
  );
}
